import * as React from 'react';
import { t } from '@reos/i18n';
import type { R3FixtureFact } from '@reos/duediligence';
import { Card, StatusBadge } from './CaseUiPrimitives.js';
import {
  KN_TEST_WATERMARK,
  NAHLIZENI_DO_KN_PARCEL_URL,
  buildParcelPanelRows,
  municipalityWebsiteFromFacts,
  plombaWarningFromFacts,
  type ParcelPanelRow,
  type ParcelPanelRowKey,
  type PlombaWarning,
} from '../lib/parcel-panel-rows.js';
import { panoramaImageUrl } from '../lib/parcel-map-api.js';
import {
  caseExportMap,
  caseHouse3dModel,
  type CaseHouseModel,
  type CaseMapSummary,
} from '../lib/case-maps-api.js';
import {
  ParcelExportError,
  downloadMapPng,
  downloadParcelOverviewPdf,
  parcelExportFileName,
} from '../lib/parcel-export.js';
import { reportClientError } from '../lib/client-error-reporter.js';
import { useSession } from '../stores/session.js';

const NOT_DETERMINED = '—';

const ROW_LABEL_KEY: Record<ParcelPanelRowKey, Parameters<typeof t>[0]> = {
  parcelLabel: 'webAdmin.parcelMap.panel.identity.parcelLabel',
  landType: 'webAdmin.parcelMap.panel.landType',
  landUse: 'webAdmin.parcelMap.panel.landUse',
  areaKn: 'webAdmin.parcelMap.panel.area.kn',
  areaGeometry: 'webAdmin.parcelMap.panel.area.geometry',
  areaMatch: 'webAdmin.parcelMap.panel.area.match',
  protection: 'webAdmin.parcelMap.panel.protection',
  lvNumber: 'webAdmin.parcelMap.panel.lvNumber',
  knProtection: 'webAdmin.parcelMap.panel.knProtection',
  bpej: 'webAdmin.parcelMap.panel.bpej',
  radon: 'webAdmin.parcelMap.panel.risks',
  flood: 'webAdmin.parcelMap.panel.risks',
  accessRoad: 'webAdmin.parcelMap.panel.utilities',
  upFunction: 'webAdmin.parcelMap.panel.upFunction',
  building: 'webAdmin.parcelMap.panel.buildingHeading',
  municipality: 'webAdmin.parcelMap.panel.municipalityInfo',
};

/**
 * Zobrazeni hodnoty radku — JEDNOTKY A SLOVA NAD HOTOVYM TEXTEM.
 *
 * Tvar faktu se tu uz nerozebira: od QAFIX10 ho na text prevadi
 * `parcelRowValueText` v `parcel-panel-rows.ts` (jeden klic = jedno
 * formatovani pro obrazovku i pro PDF). Nove formatovani podle KLICE FAKTU
 * patri tam, ne sem — jinak vznikne druha kopie teze pravdy.
 */
function rowDisplayValue(row: ParcelPanelRow): string {
  if (row.key === 'areaKn' || row.key === 'areaGeometry') {
    return row.value === null ? NOT_DETERMINED : `${row.value} m²`;
  }
  if (row.key === 'areaMatch') {
    if (row.value === null) return NOT_DETERMINED;
    if (row.value === 'match') return t('webAdmin.parcelMap.panel.area.match', { locale: 'cs' });
    const diff = row.value.split(':')[1] ?? '';
    return t('webAdmin.parcelMap.panel.area.mismatch', { locale: 'cs', values: { diff } });
  }
  // Krok KN1: prazdny seznam ochran je ODPOVED („bez zápisu"), ne pomlcka.
  if (row.key === 'knProtection' && row.value === 'none') {
    return t('webAdmin.parcelMap.panel.knProtectionNone', { locale: 'cs' });
  }
  return row.value ?? NOT_DETERMINED;
}

/**
 * Krok KN1 bod 3 — VYRAZNE UPOZORNENI na bezici rizeni (plombu).
 *
 * Stoji NAD radky panelu zamerne: plomba muze zmenit pravni stav mezi
 * rezervaci a podpisem, takze makler ji ma videt driv, nez zacne cist
 * tabulku. U kazdeho rizeni je cislo v tvaru z Nahlizeni (`V-1234/2026`)
 * a datum prijeti, pod nimi provenance „ČÚZK – API KN, stav k ...".
 */
function PlombaNotice({ warning }: { readonly warning: PlombaWarning }): React.JSX.Element {
  return (
    <div
      role="alert"
      style={{
        border: '1px solid var(--rh-warn-border, var(--rh-rule))',
        background: 'var(--rh-warn-bg, transparent)',
        borderRadius: 8,
        padding: 10,
        marginBottom: 12,
      }}
      data-fact-id={warning.factId ?? undefined}
      data-testid="parcel-panel-plomba"
    >
      <strong style={{ fontSize: 'var(--rh-size-footnote)' }}>
        {t('webAdmin.parcelMap.plomba.heading', { locale: 'cs' })}
      </strong>
      <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}>
        {warning.proceedings.map((item) => (
          <li key={item.label} data-testid={`parcel-panel-plomba-item-${item.label}`}>
            {item.receivedAt === null
              ? t('webAdmin.parcelMap.plomba.itemNoDate', { locale: 'cs', values: { label: item.label } })
              : t('webAdmin.parcelMap.plomba.item', {
                  locale: 'cs',
                  values: { label: item.label, date: item.receivedAt.slice(0, 10) },
                })}
          </li>
        ))}
      </ul>
      <div
        style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-text-muted)', marginTop: 4 }}
        data-testid="parcel-panel-plomba-source"
      >
        {warning.dataAsOf === null
          ? t('webAdmin.parcelMap.plomba.sourceUnknown', { locale: 'cs' })
          : t('webAdmin.parcelMap.plomba.source', { locale: 'cs', values: { date: warning.dataAsOf.slice(0, 10) } })}
      </div>
    </div>
  );
}

function PanelRow({ row }: { readonly row: ParcelPanelRow }): React.JSX.Element {
  return (
    <div
      style={{ borderTop: '1px solid var(--rh-rule)', padding: '7px 0', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}
      data-testid={`parcel-panel-row-${row.key}`}
      data-fact-id={row.factId ?? undefined}
    >
      <span style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', minWidth: 150 }}>{t(ROW_LABEL_KEY[row.key], { locale: 'cs' })}</span>
      <span style={{ fontSize: 'var(--rh-size-footnote)', flex: 1 }}>{rowDisplayValue(row)}</span>
      {row.status !== null && <StatusBadge status={row.status} />}
    </div>
  );
}

const PANORAMA_DIRECTIONS = ['point', 0, 90, 180, 270] as const;

function PanoramaThumb({ caseId, yaw }: { readonly caseId: string; readonly yaw: (typeof PANORAMA_DIRECTIONS)[number] }): React.JSX.Element {
  const [unavailable, setUnavailable] = React.useState(false);
  const label = t(`webAdmin.parcelMap.panorama.direction.${yaw}` as Parameters<typeof t>[0], { locale: 'cs' });
  return (
    <div style={{ width: yaw === 'point' ? 220 : 90 }} data-testid={`parcel-panel-panorama-${yaw}`}>
      {unavailable ? (
        <div
          style={{ width: '100%', height: yaw === 'point' ? 130 : 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--rh-rule)', borderRadius: 8, fontSize: 'var(--rh-size-tag)', color: 'var(--rh-icon-muted)', textAlign: 'center', padding: 4 }}
          data-testid={`parcel-panel-panorama-${yaw}-unavailable`}
        >
          {t('webAdmin.parcelMap.panorama.unavailable', { locale: 'cs' })}
        </div>
      ) : (
        <img
          src={panoramaImageUrl(caseId, yaw)}
          alt={label}
          onError={() => { setUnavailable(true); }}
          style={{ width: '100%', height: yaw === 'point' ? 130 : 60, objectFit: 'cover', borderRadius: 8 }}
        />
      )}
      <div style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-text-muted)', textAlign: 'center', marginTop: 2 }}>{label}</div>
    </div>
  );
}

/**
 * Krok NAP16 (A31) — NAHLED 3D MODELU DOMU V MAPE.
 *
 * Obrazek je render z Blenderu (pohled z ptaci perspektivy) a odkaz vede na
 * model GLB teze ulohy. Pod obrazkem stoji STITEK PUVODU — schematicky model
 * z registru — tatáz veta, jakou nese popiska v reportu
 * (`houseSceneOriginLabel(false)`): render nesmi vypadat jako projekt domu.
 */
function House3dPreview({ model }: { readonly model: CaseHouseModel }): React.JSX.Element {
  return (
    <div data-testid="parcel-panel-house3d-preview" style={{ marginBottom: 12 }}>
      <img
        src={model.imageUrl}
        alt={t('webAdmin.parcelMap.chips.house3d', { locale: 'cs' })}
        style={{ width: '100%', borderRadius: 8, display: 'block' }}
        data-testid="parcel-panel-house3d-image"
      />
      <div
        style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-muted)', marginTop: 4 }}
        data-testid="parcel-panel-house3d-origin"
      >
        {t('webAdmin.parcelMap.house3d.origin', { locale: 'cs' })}
      </div>
      <a
        href={model.modelUrl}
        target="_blank"
        rel="noreferrer"
        className="rh-btn"
        style={{ marginTop: 6, display: 'inline-block' }}
        data-testid="parcel-panel-house3d-download"
      >
        {t('webAdmin.parcelMap.house3d.download', { locale: 'cs' })}
      </a>
      {model.licenseNote !== null && (
        <div
          style={{ fontSize: 'var(--rh-size-footnote)', color: 'var(--rh-muted)', marginTop: 4 }}
          data-testid="parcel-panel-house3d-license"
        >
          {model.licenseNote}
        </div>
      )}
    </div>
  );
}

function PanoramaPreview({ caseId }: { readonly caseId: string }): React.JSX.Element {
  return (
    <div data-testid="parcel-panel-panorama-preview" style={{ marginBottom: 12 }}>
      <PanoramaThumb caseId={caseId} yaw="point" />
      <div className="flex gap-2 mt-2 flex-wrap">
        {PANORAMA_DIRECTIONS.filter((yaw): yaw is 0 | 90 | 180 | 270 => yaw !== 'point').map((yaw) => (
          <PanoramaThumb key={yaw} caseId={caseId} yaw={yaw} />
        ))}
      </div>
    </div>
  );
}

/**
 * Krok KN4 bod 2 — „ZOBRAZIT V KATASTRU" MISTO UDAJU Z KATASTRU.
 *
 * § 53 katastralniho zakona: udaje katastru se smi sirit jen se souhlasem
 * ČÚZK. Panel proto radky LV, zpusobu ochrany a BPEJ neukazuje a misto nich
 * posila maklere do Nahlizeni do KN, kde si je overi zdarma. Veta nad
 * odkazem rika PROC — bez ni by to vypadalo jako rozbita funkce.
 *
 * Odkaz vede na VYHLEDAVACI stranku parcely: Nahlizeni adresuje konkretni
 * objekt jen vnitrnim identifikatorem ISKN, ktery nezname (overeno 13. 10.
 * 2026, viz `kn-output-policy.ts`), a mrtvy deep-link je horsi nez jedno
 * opsani parcelniho cisla, ktere makler vidi v radku nad odkazem.
 */
function CadastreLinkNotice(): React.JSX.Element {
  return (
    <div style={{ marginBottom: 12 }} data-testid="parcel-panel-cadastre-link">
      <p className="rh-a2-muted" style={{ marginBottom: 6 }}>
        {t('webAdmin.parcelMap.panel.cadastreOnlyLink', { locale: 'cs' })}
      </p>
      <a
        href={NAHLIZENI_DO_KN_PARCEL_URL}
        target="_blank"
        rel="noreferrer"
        className="rh-btn"
        data-testid="parcel-panel-chip-cadastre"
      >
        {t('webAdmin.parcelMap.chips.cadastre', { locale: 'cs' })}
      </a>
    </div>
  );
}

export interface ParcelPanelProps {
  readonly caseId: string;
  readonly facts: readonly R3FixtureFact[];
  readonly geometryAreaM2: number | null;
}

/**
 * Krok M1 bod 3 — panel parcely (vpravo v zalozce geometrie). Radky se
 * stavem duvery (`StatusBadge`, stejny vzor jako zbytek detailu zakazky) a
 * odkazem na evidence id (`data-fact-id`) - odvozene z `detail.facts`,
 * zadna vymyslena hodnota (viz `parcel-panel-rows.ts`).
 *
 * M1b — chipy Panorama (proxy na Mapy.com, klic jen na serveru - viz
 * `panoramaImageUrl`/`dd-map-tools.ts`) a Web obce (fakt
 * `municipality.website`, adapter Seznam OVM).
 */
export function ParcelPanel({ caseId, facts, geometryAreaM2 }: ParcelPanelProps): React.JSX.Element {
  /**
   * Krok KN4 bod 2 — UDAJE Z KATASTRU JEN ODKAZEM. Flag `knOutputs` jde
   * z `/api/me` (`KN_OUTPUTS_ENABLED` na serveru), takze panel nemuze ukazat
   * nic, co by server smel zapsat a nesmel sirit. Vychozi `false`.
   */
  const knOutputs = useSession((state) => state.knOutputs);
  /**
   * Krok KNT1 bod 2 — VODOZNAK I NA OBRAZOVCE. Kdyz udaje katastru vidi
   * makler jen pres vyjimku nasi interni testovaci kancelare, musi na panelu
   * videt, ze je sirit nesmi — snimek obrazovky z adminu se rozesila stejne
   * snadno jako PDF.
   */
  const knOutputsTest = useSession((state) => state.knOutputsTest);
  const rows = React.useMemo(
    () => buildParcelPanelRows(facts, geometryAreaM2, knOutputs),
    [facts, geometryAreaM2, knOutputs],
  );
  const municipalityWebsite = React.useMemo(() => municipalityWebsiteFromFacts(facts), [facts]);
  const parcelLabel = rows.find((row) => row.key === 'parcelLabel')?.value ?? null;
  const plomba = React.useMemo(
    () => plombaWarningFromFacts(facts, parcelLabel, knOutputs),
    [facts, parcelLabel, knOutputs],
  );
  const [panoramaOpen, setPanoramaOpen] = React.useState(false);
  /**
   * Krok NAP16 (A31) — CIP 3D MODELU VZNIKNE JEN KDYZ MODEL EXISTUJE.
   *
   * Prepinac `HOUSE_3D_ENABLED` je vychozi vypnuty, takze vetsina zakazek
   * model nema; cip, ktery by po kliknuti ukazal prazdno, je slib navic.
   * Selhani cteni je `null` (zadny cip), ne chyba panelu — tataz uvaha jako
   * u nahledu vyrezu (`case-maps-api.ts`).
   */
  const [house3d, setHouse3d] = React.useState<CaseHouseModel | null>(null);
  const [house3dOpen, setHouse3dOpen] = React.useState(false);
  /**
   * Krok MAP3b (`E4`/`J1`) — EXPORT. Hotovy vyrez pripadu je podklad obou
   * exportu: PNG je on sam, do PDF se vklada. Kdyz zadny neni (`null`),
   * tlacitko PNG se nenabizi a PDF vznikne bez mapy — nic se nepredstira
   * (T1b), tataz uvaha jako u cipu 3D modelu vyse.
   */
  const [exportMap, setExportMap] = React.useState<CaseMapSummary | null>(null);
  const [exportError, setExportError] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    void caseHouse3dModel(caseId).then(
      (model) => {
        if (active) setHouse3d(model);
      },
      () => {
        if (active) setHouse3d(null);
      },
    );
    return () => {
      active = false;
    };
  }, [caseId]);
  React.useEffect(() => {
    let active = true;
    void caseExportMap(caseId).then(
      (map) => {
        if (active) setExportMap(map);
      },
      () => {
        if (active) setExportMap(null);
      },
    );
    return () => {
      active = false;
    };
  }, [caseId]);

  /** Radky PDF = presne to, co je videt v panelu (vzor O5b, viz route). */
  const overviewRows = React.useMemo(
    () =>
      rows.map((row) => ({
        label: t(ROW_LABEL_KEY[row.key], { locale: 'cs' }),
        value: rowDisplayValue(row),
      })),
    [rows],
  );

  /**
   * Krok QAFIX10 (QA-ZAV-N2) — HLASKA MUSI RIKAT PRAVDU.
   *
   * „Zkuste to prosím znovu" plati pro vypadek site nebo sluzby. U VALIDACNI
   * chyby (`400`/`422`: dokument nektery udaj panelu neprijal) opakovani
   * nikdy nepomuze, takze obrazovka rekne, ze je to na nas, a chybu zapise
   * do `/v1/client-errors` — jinak o ni nikdo nevi a makler klika dal.
   */
  async function runExport(apiPath: string, task: () => Promise<void>): Promise<void> {
    setExportError(null);
    setExporting(true);
    try {
      await task();
    } catch (error) {
      const invalid = error instanceof ParcelExportError && error.isInvalidRequest;
      if (error instanceof ParcelExportError) {
        reportClientError({
          kind: 'api',
          message: `parcel-export: ${error.message}`,
          apiPath,
          apiStatus: error.status,
        });
      }
      setExportError(
        t(invalid ? 'webAdmin.parcelMap.export.failedInvalid' : 'webAdmin.parcelMap.export.failed', {
          locale: 'cs',
        }),
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <Card heading={t('webAdmin.parcelMap.panel.heading', { locale: 'cs' })} testId="parcel-panel">
      {plomba !== null && <PlombaNotice warning={plomba} />}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="parcel-panel-chips">
        <button
          type="button"
          className="rh-btn"
          aria-pressed={panoramaOpen}
          onClick={() => { setPanoramaOpen((v) => !v); }}
          data-testid="parcel-panel-chip-panorama"
        >
          {t('webAdmin.parcelMap.chips.panorama', { locale: 'cs' })}
        </button>
        {municipalityWebsite !== null ? (
          <a
            href={municipalityWebsite.url}
            target="_blank"
            rel="noreferrer"
            className="rh-btn"
            data-fact-id={municipalityWebsite.factId ?? undefined}
            data-testid="parcel-panel-chip-municipality-website"
          >
            {t('webAdmin.parcelMap.chips.municipalityWeb', { locale: 'cs' })}
          </a>
        ) : (
          <span
            className="rh-btn"
            aria-disabled="true"
            title={t('webAdmin.parcelMap.municipalityWebsite.missing', { locale: 'cs' })}
            data-testid="parcel-panel-chip-municipality-website-missing"
          >
            {t('webAdmin.parcelMap.chips.municipalityWeb', { locale: 'cs' })}
          </span>
        )}
        {house3d !== null && (
          <button
            type="button"
            className="rh-btn"
            aria-pressed={house3dOpen}
            onClick={() => { setHouse3dOpen((v) => !v); }}
            data-testid="parcel-panel-chip-house3d"
          >
            {t('webAdmin.parcelMap.chips.house3d', { locale: 'cs' })}
          </button>
        )}
      </div>
      {/* Krok MAP3b — EXPORT: PNG vyrezu (`E4`) a PDF prehledu parcely
          s logem kancelare (`J1`). Vlastni radek, ne mezi cipy: cipy
          prepinaji, co je v panelu videt, export posila soubor ven. */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="parcel-panel-export">
        {exportMap !== null && (
          <button
            type="button"
            className="rh-btn"
            disabled={exporting}
            onClick={() => {
              void runExport('/v1/duediligence/cases/:id/maps/:mapId/image', () =>
                downloadMapPng(
                  caseId,
                  exportMap.id,
                  parcelExportFileName(parcelLabel, 'png'),
                ),
              );
            }}
            data-testid="parcel-panel-export-png"
          >
            {t('webAdmin.parcelMap.export.png', { locale: 'cs' })}
          </button>
        )}
        <button
          type="button"
          className="rh-btn"
          disabled={exporting}
          onClick={() => {
            void runExport('/v1/duediligence/cases/:id/parcel-overview.pdf', () =>
              downloadParcelOverviewPdf(caseId, {
                parcelLabel,
                rows: overviewRows,
                mapId: exportMap?.id,
                fileName: parcelExportFileName(parcelLabel, 'pdf'),
              }),
            );
          }}
          data-testid="parcel-panel-export-pdf"
        >
          {t('webAdmin.parcelMap.export.pdf', { locale: 'cs' })}
        </button>
      </div>
      {exportError !== null && (
        <p
          className="text-sm m-0 mb-2"
          style={{ color: 'var(--rh-danger)' }}
          data-testid="parcel-panel-export-error"
        >
          {exportError}
        </p>
      )}
      {panoramaOpen && <PanoramaPreview caseId={caseId} />}
      {house3dOpen && house3d !== null && <House3dPreview model={house3d} />}
      {knOutputs && knOutputsTest && (
        <div
          role="alert"
          style={{
            /**
             * Tokeny, ne barvy natvrdo (UXV4a): vodoznak musi byt citelny
             * i v tmavem rezimu, kde se obe hodnoty obraceji.
             */
            background: 'var(--rh-danger-bg)',
            color: 'var(--rh-danger)',
            border: '1px solid var(--rh-danger-dot)',
            borderRadius: 8,
            padding: '6px 10px',
            marginBottom: 12,
            fontSize: 'var(--rh-size-footnote)',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
          data-kn-test="1"
          data-testid="parcel-panel-kn-test-watermark"
        >
          {KN_TEST_WATERMARK}
        </div>
      )}
      {!knOutputs && <CadastreLinkNotice />}
      <div>
        {rows.map((row) => (
          <PanelRow key={row.key} row={row} />
        ))}
      </div>
    </Card>
  );
}
